import * as THREE from 'three';
import { Entity } from './Entity.js';
import { InputManager } from '../input/Input.js';
import { Room } from '../level/Room.js';
import { Part, SlotType, WeaponPartData, ProjectileModifiers } from '../parts/Part.js';
import { createPart, createPartMesh } from '../parts/PartRegistry.js';
import { Projectile, type ProjectileConfig } from './Projectile.js';
import { SparkEffect } from './SparkEffect.js';
import type { Game } from '../game/Game.js';
import type { Circle } from '../systems/Collision.js';
import { DamageDirection } from '../systems/Damage.js';
import { audio } from '../audio/Audio.js';

/**
 * Player-controlled mech entity.
 *
 * Phase 2: The mech now has four slots (head, weapon, core, transport).
 * Each slot accepts a Part that affects behavior and appearance.
 *
 * Slots:
 * - Head: Perception, targeting (losing = death)
 * - Weapon: Damage output (losing = can't attack)
 * - Core: HP, energy (losing = reduced resources)
 * - Transport: Movement, dash (losing = crawl mode)
 *
 * Visual:
 * - Composite mesh built from equipped parts
 * - Head-only mode (cone) when no transport equipped
 * - Full mech appearance when parts assembled
 *
 * Movement:
 * - Slow "hop" when head-only (no transport)
 * - Speed determined by transport part when equipped
 *
 * Aiming:
 * - Head continuously tracks mouse position
 */
export class Mech extends Entity {
  private input: InputManager;
  private room: Room;
  private game: Game;

  // ========== PART SLOTS ==========
  // null = empty slot

  /** Head part - perception and targeting (losing = death) */
  private head: Part | null = null;

  /** Weapon part - damage output (losing = can't attack) */
  private weapon: Part | null = null;

  /** Core part - HP and energy (losing = reduced resources) */
  private core: Part | null = null;

  /** Transport part - movement and dash (losing = crawl mode) */
  private transport: Part | null = null;

  // ========== ENERGY SYSTEM ==========

  /** Current energy level */
  private energy: number = 100;

  /** Maximum energy capacity */
  private maxEnergy: number = 100;

  /** Default energy regeneration rate when no core equipped (units per second) */
  private readonly defaultEnergyRegen: number = 1;

  // ========== HP SYSTEM ==========
  // HP is now tracked via part durability.
  // getHP() and getMaxHP() calculate values from equipped parts.

  /**
   * Movement speed when in head-only mode (units per second).
   * This is overridden by transport part when equipped.
   */
  private readonly headOnlyMoveSpeed: number = 2;

  // ========== DASH SYSTEM ==========

  /** Whether currently performing a dash */
  private isDashing: boolean = false;

  /** Remaining dash duration in seconds (counts down to 0) */
  private dashTimer: number = 0;

  /** Direction vector for current dash */
  private dashDirection: { x: number; y: number } = { x: 0, y: 0 };

  /** Fixed duration for all dashes (seconds) */
  private readonly dashDuration: number = 0.15;

  // ========== FUSION PULSE SYSTEM ==========

  /** Fusion scale pulse timer in seconds (counts down to 0) */
  private fusionScaleTimer: number = 0;

  /** Duration of fusion scale pulse effect */
  private readonly fusionScaleDuration: number = 0.3;

  // ========== WEAPON SYSTEM ==========

  /** Fire cooldown timer in seconds (counts down to 0) */
  private fireCooldown: number = 0;

  /** Muzzle flash light for weapon firing */
  private muzzleFlash: THREE.PointLight | null = null;

  /** Timer for muzzle flash duration (seconds) */
  private muzzleFlashTimer: number = 0;

  /**
   * Collision radius for mech (approximate body size).
   * Mechs use a circle collision shape.
   */
  private readonly collisionRadius: number = 0.5;

  constructor(input: InputManager, room: Room, game: Game, x: number = 0, y: number = 0) {
    super(x, y, 0);
    this.input = input;
    this.room = room;
    this.game = game;

    // Initialize with just a scanner head (default starting equipment)
    this.head = createPart('scanner');

    // Build the initial mesh (head-only mode)
    this.rebuildMesh();

    // Sync initial transform
    this.updateMeshTransform();
  }

  // ========== PART SLOT MANAGEMENT ==========

  /**
   * Equip a part to the appropriate slot.
   *
   * The part's slot type determines which slot it goes into.
   * If a part is already equipped in that slot, it is returned (for dropping).
   *
   * @param part - The part to equip
   * @returns The old part that was replaced, or null if slot was empty
   */
  equipPart(part: Part): Part | null {
    const slot = part.data.slot;
    let oldPart: Part | null = null;

    // Get the old part and equip the new one
    switch (slot) {
      case 'head':
        oldPart = this.head;
        this.head = part;
        break;
      case 'weapon':
        oldPart = this.weapon;
        this.weapon = part;
        break;
      case 'core':
        oldPart = this.core;
        this.core = part;
        break;
      case 'transport':
        oldPart = this.transport;
        this.transport = part;
        break;
    }

    // Rebuild the visual
    this.rebuildMesh();

    return oldPart;
  }

  /**
   * Remove a part from a slot.
   *
   * @param slot - The slot to empty
   * @returns The part that was removed, or null if slot was already empty
   */
  removePart(slot: SlotType): Part | null {
    let removed: Part | null = null;

    switch (slot) {
      case 'head':
        removed = this.head;
        this.head = null;
        break;
      case 'weapon':
        removed = this.weapon;
        this.weapon = null;
        break;
      case 'core':
        removed = this.core;
        this.core = null;
        break;
      case 'transport':
        removed = this.transport;
        this.transport = null;
        break;
    }

    // Rebuild the visual
    this.rebuildMesh();

    return removed;
  }

  /**
   * Get the current part in a slot.
   *
   * @param slot - The slot to query
   * @returns The part in the slot, or null if empty
   */
  getPart(slot: SlotType): Part | null {
    switch (slot) {
      case 'head':
        return this.head;
      case 'weapon':
        return this.weapon;
      case 'core':
        return this.core;
      case 'transport':
        return this.transport;
    }
  }

  /**
   * Check if a slot has a part equipped.
   *
   * @param slot - The slot to check
   * @returns true if the slot has a part, false if empty
   */
  hasPartInSlot(slot: SlotType): boolean {
    return this.getPart(slot) !== null;
  }

  // ========== ENERGY SYSTEM METHODS ==========

  /**
   * Get the current energy regeneration rate from the core part.
   *
   * @returns Energy regeneration in units per second
   */
  private getEnergyRegen(): number {
    if (this.core && this.core.data.slot === 'core') {
      return (this.core.data as import('../parts/Part.js').CorePartData).energyRegen;
    }
    return this.defaultEnergyRegen;
  }

  /**
   * Use energy for an action (e.g., firing a weapon).
   *
   * @param amount - Amount of energy to use
   * @returns true if energy was used successfully, false if not enough energy
   */
  useEnergy(amount: number): boolean {
    if (this.energy >= amount) {
      this.energy -= amount;
      return true;
    }
    return false;
  }

  /**
   * Get the current energy level.
   *
   * @returns Current energy
   */
  getEnergy(): number {
    return this.energy;
  }

  /**
   * Get the maximum energy capacity.
   *
   * @returns Maximum energy
   */
  getMaxEnergy(): number {
    return this.maxEnergy;
  }

  // ========== HP SYSTEM METHODS ==========

  /**
   * Check if the mech is currently invulnerable.
   *
   * @returns true if invulnerable (e.g., during dash), false otherwise
   */
  private isInvulnerable(): boolean {
    return this.isDashing;
  }

  /**
   * Take damage and route it through parts based on direction.
   *
   * Damage flows through parts in order based on direction:
   * - Front: Weapon → Core → Head
   * - Rear: Transport → Core → Head
   * - Area: Weapon → Transport → Core → Head (distributed)
   *
   * Parts break when durability reaches 0 and are dropped as damaged pickups.
   *
   * @param amount - Amount of damage to take
   * @param direction - Direction from which damage is received (default: 'front')
   * @returns true if still alive, false if dead (head destroyed)
   */
  takeDamage(amount: number, direction: DamageDirection = 'front'): boolean {
    // No damage during i-frames
    if (this.isInvulnerable()) {
      return true;
    }

    // Play hit sound
    audio.playerHit();

    // Get damage order based on direction
    const damageOrder = this.getDamageOrder(direction);

    let remaining = amount;

    // Route damage through parts in order
    for (const slot of damageOrder) {
      const part = this.getPart(slot);
      if (!part) continue;

      // Calculate how much damage this part can absorb
      const absorbed = Math.min(remaining, part.durability);
      part.durability -= absorbed;
      remaining -= absorbed;

      // Check if part broke
      if (part.durability <= 0) {
        this.breakPart(slot);

        // If head broke, mech is dead
        if (slot === 'head') {
          return false;
        }
      }

      // All damage absorbed
      if (remaining <= 0) break;
    }

    // Any remaining damage that couldn't be absorbed means death
    // This should only happen if all parts are destroyed
    if (remaining > 0) {
      return false;
    }

    return true; // Still alive
  }

  /**
   * Get the damage routing order based on direction.
   *
   * @param direction - Direction from which damage is received
   * @returns Array of slots in damage priority order
   */
  private getDamageOrder(direction: DamageDirection): SlotType[] {
    switch (direction) {
      case 'front':
        return ['weapon', 'core', 'head'];
      case 'rear':
        return ['transport', 'core', 'head'];
      case 'area':
        return ['weapon', 'transport', 'core', 'head'];
    }
  }

  /**
   * Break a part, remove it from the mech, and drop it as a damaged pickup.
   *
   * @param slot - The slot of the part to break
   */
  private breakPart(slot: SlotType): void {
    const part = this.getPart(slot);
    if (!part) return;

    // Remove from mech
    this.removePart(slot);

    // Spawn spark effect at break location
    const sparkEffect = new SparkEffect(this.position.x, this.position.y);
    this.game.addEntity(sparkEffect);

    // Spawn as damaged pickup at mech position
    // The part retains its reduced durability (0 or near-0)
    this.game.spawnPickup(this.position.x, this.position.y, part);

    // Visual will be rebuilt by removePart()
  }

  /**
   * Get the current HP (sum of all part durabilities).
   *
   * @returns Current HP across all parts
   */
  getHP(): number {
    let total = 0;
    if (this.head) total += this.head.durability;
    if (this.weapon) total += this.weapon.durability;
    if (this.core) total += this.core.durability;
    if (this.transport) total += this.transport.durability;
    return total;
  }

  /**
   * Get the maximum HP (sum of all part max durabilities).
   *
   * @returns Maximum HP across all parts
   */
  getMaxHP(): number {
    let total = 0;
    if (this.head) total += this.head.data.maxDurability;
    if (this.weapon) total += this.weapon.data.maxDurability;
    if (this.core) total += this.core.data.maxDurability;
    if (this.transport) total += this.transport.data.maxDurability;
    return total;
  }

  /**
   * Check if the mech is still alive.
   *
   * @returns true if head exists and has durability, false otherwise
   */
  isAlive(): boolean {
    return this.head !== null && this.head.durability > 0;
  }

  // ========== MESH BUILDING ==========

  /**
   * Rebuild the mech's visual representation based on equipped parts.
   *
   * This clears the existing mesh and constructs a new one from parts.
   * Called whenever parts are equipped, removed, or swapped.
   *
   * Part positioning:
   * - Transport: Base at y=0 (ground level)
   * - Core: Center of body at y=0.5
   * - Head: Top at y=1.0
   * - Weapon: To the right side at y=0.5
   */
  private rebuildMesh(): void {
    // Clean up existing mesh
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.mesh.clear();
    } else {
      this.mesh = new THREE.Group();
    }

    // Build composite mesh from equipped parts
    // Order matters for visual layering

    // 1. Transport (base/body) - ground level
    if (this.transport) {
      const transportMesh = createPartMesh(this.transport);
      transportMesh.position.set(0, 0.2, 0); // Slightly above ground
      this.mesh.add(transportMesh);
      this.transport.mesh = transportMesh;
    }

    // 2. Core (energy/HP) - center of body
    if (this.core) {
      const coreMesh = createPartMesh(this.core);
      coreMesh.position.set(0, 0.5, 0); // Center height
      this.mesh.add(coreMesh);
      this.core.mesh = coreMesh;
    }

    // 3. Head - top of the mech
    if (this.head) {
      const headMesh = createPartMesh(this.head);
      headMesh.position.set(0, 1.0, 0); // Top position
      this.mesh.add(headMesh);
      this.head.mesh = headMesh;
    }

    // 4. Weapon - to the side
    if (this.weapon) {
      const weaponMesh = createPartMesh(this.weapon);
      weaponMesh.position.set(0.6, 0.5, 0.2); // Right side, center height, slightly forward
      this.mesh.add(weaponMesh);
      this.weapon.mesh = weaponMesh;
    }
  }

  update(dt: number): void {
    // Convert dt from milliseconds to seconds
    const dtSec = dt / 1000;

    // Regenerate energy based on core part
    const regen = this.getEnergyRegen() * dtSec;
    this.energy = Math.min(this.energy + regen, this.maxEnergy);

    // Update dash timers
    this.updateDashTimers(dtSec);

    // Update weapon cooldown
    if (this.fireCooldown > 0) {
      this.fireCooldown -= dtSec;
    }

    // Update muzzle flash
    this.updateMuzzleFlash(dtSec);

    // Update fusion pulse animation
    this.updateFusionPulse(dtSec);

    this.handleMovement(dt);
    this.handleAiming();
    this.handleShooting(dtSec);
    this.updateMeshTransform();
  }

  // ========== BEHAVIOR ==========

  /**
   * Handle WASD movement input and update position.
   *
   * Movement speed is determined by equipped transport part.
   * Without transport, uses slow "head-only" hop speed.
   *
   * Also handles dash input and dash movement.
   *
   * After movement, position is clamped to room bounds for wall collision.
   */
  private handleMovement(dt: number): void {
    // Convert dt from milliseconds to seconds
    const dtSec = dt / 1000;

    // If currently dashing, execute dash movement
    if (this.isDashing) {
      this.executeDash(dtSec);
      return;
    }

    // Calculate desired movement direction from input
    let vx = 0;
    let vy = 0;

    // Movement mapped to screen directions with isometric camera
    // Camera looks from +Z toward -Z, so screen "up" = -Y in game coords
    if (this.input.isKeyDown('w')) {
      vy -= 1; // Move up on screen (-Y in game coords with tilted camera)
    }
    if (this.input.isKeyDown('s')) {
      vy += 1; // Move down on screen (+Y in game coords)
    }
    if (this.input.isKeyDown('a')) {
      vx -= 1; // Move left (-X)
    }
    if (this.input.isKeyDown('d')) {
      vx += 1; // Move right (+X)
    }

    // Normalize direction vector
    const magnitude = Math.sqrt(vx * vx + vy * vy);
    if (magnitude > 0) {
      vx = vx / magnitude;
      vy = vy / magnitude;
    }

    // Check for dash input (Space or Shift)
    if ((this.input.isKeyDown(' ') || this.input.isKeyDown('Shift')) && this.canDash() && magnitude > 0) {
      this.startDash(vx, vy);
      return;
    }

    // Normal movement
    if (magnitude > 0) {
      const moveSpeed = this.getMoveSpeed();
      this.position.x += vx * moveSpeed * dtSec;
      this.position.y += vy * moveSpeed * dtSec;
    }

    // Clamp position to room bounds (wall collision)
    this.position = this.room.clampPosition(this.position, this.collisionRadius);
  }

  /**
   * Get current movement speed based on equipped transport.
   *
   * @returns Movement speed in units per second
   */
  private getMoveSpeed(): number {
    if (this.transport && this.transport.data.slot === 'transport') {
      return (this.transport.data as import('../parts/Part.js').TransportPartData).moveSpeed;
    }
    return this.headOnlyMoveSpeed;
  }

  /**
   * Get current vision radius based on equipped head.
   *
   * Vision radius determines the size of the fog of war circle.
   * Different heads provide different vision capabilities:
   * - Scanner: 12 units (medium vision, can see HP)
   * - Tracker: 10 units (smallest vision, can see trails)
   * - Spotter: 15 units (largest vision)
   *
   * @returns Vision radius in world units
   */
  getVisionRadius(): number {
    if (!this.head) {
      // Default small vision if no head (shouldn't happen in normal gameplay)
      return 8;
    }
    return (this.head.data as import('../parts/Part.js').HeadPartData).visionRadius;
  }

  // ========== FUSION PULSE METHODS ==========

  /**
   * Trigger fusion scale pulse animation.
   *
   * Called when a fusion is successful to create a visual transformation effect.
   */
  triggerFusionPulse(): void {
    this.fusionScaleTimer = this.fusionScaleDuration;
  }

  /**
   * Update fusion scale pulse animation.
   *
   * @param dtSec - Delta time in seconds
   */
  private updateFusionPulse(dtSec: number): void {
    if (this.fusionScaleTimer > 0) {
      this.fusionScaleTimer -= dtSec;

      // Calculate pulse scale using sine wave
      const t = this.fusionScaleTimer / this.fusionScaleDuration;
      const scale = 1 + Math.sin(t * Math.PI) * 0.3;
      this.mesh.scale.setScalar(scale);
    } else {
      // Ensure scale is reset to 1.0 when pulse completes
      this.mesh.scale.setScalar(1);
    }
  }

  // ========== DASH SYSTEM METHODS ==========

  /**
   * Get dash distance from equipped transport.
   *
   * @returns Dash distance in units, or 0 if no transport equipped
   */
  private getDashDistance(): number {
    if (this.transport && this.transport.data.slot === 'transport') {
      return (this.transport.data as import('../parts/Part.js').TransportPartData).dashDistance;
    }
    return 0;
  }

  /**
   * Get dash duration (fixed for all transports).
   *
   * @returns Dash duration in seconds
   */
  private getDashDuration(): number {
    return this.dashDuration;
  }

  /**
   * Check if dash is currently available.
   *
   * @returns true if dash can be activated, false otherwise
   */
  private canDash(): boolean {
    if (!this.transport || this.isDashing) {
      return false;
    }
    // Check if we have enough energy
    const energyCost = this.getDashEnergyCost();
    return this.energy >= energyCost;
  }

  /**
   * Get dash energy cost from equipped transport.
   */
  private getDashEnergyCost(): number {
    if (this.transport && this.transport.data.slot === 'transport') {
      return (this.transport.data as import('../parts/Part.js').TransportPartData).dashEnergyCost;
    }
    return 20; // Default cost
  }

  /**
   * Update dash-related timers.
   *
   * @param dtSec - Delta time in seconds
   */
  private updateDashTimers(dtSec: number): void {
    // Count down dash timer (dash duration)
    if (this.isDashing && this.dashTimer > 0) {
      this.dashTimer -= dtSec;
      if (this.dashTimer <= 0) {
        this.isDashing = false;
        this.dashTimer = 0;
      }
    }
  }

  /**
   * Start a dash in the given direction.
   *
   * @param dirX - Normalized X direction
   * @param dirY - Normalized Y direction
   */
  private startDash(dirX: number, dirY: number): void {
    // Consume energy
    const energyCost = this.getDashEnergyCost();
    this.energy -= energyCost;

    this.isDashing = true;
    this.dashTimer = this.getDashDuration();
    this.dashDirection.x = dirX;
    this.dashDirection.y = dirY;

    // Play dash sound
    audio.dash();
  }

  /**
   * Execute dash movement for the current frame.
   *
   * @param dtSec - Delta time in seconds
   */
  private executeDash(dtSec: number): void {
    const dashDistance = this.getDashDistance();
    const dashSpeed = dashDistance / this.getDashDuration();

    // Move in dash direction at dash speed
    this.position.x += this.dashDirection.x * dashSpeed * dtSec;
    this.position.y += this.dashDirection.y * dashSpeed * dtSec;

    // Clamp position to room bounds
    this.position = this.room.clampPosition(this.position, this.collisionRadius);
  }

  // ========== MUZZLE FLASH SYSTEM ==========

  /**
   * Show muzzle flash at weapon position.
   */
  private showMuzzleFlash(): void {
    if (!this.muzzleFlash) {
      // Create muzzle flash light on first use
      this.muzzleFlash = new THREE.PointLight(0xffff00, 2, 3);
      this.mesh.add(this.muzzleFlash);
      // Position at weapon location (right side, center height, slightly forward)
      this.muzzleFlash.position.set(0.6, 0.5, 0.2);
    }

    // Reset timer and intensity
    this.muzzleFlashTimer = 0.05; // 50ms flash
    this.muzzleFlash.intensity = 2;
  }

  /**
   * Update muzzle flash timer and fade out.
   *
   * @param dtSec - Delta time in seconds
   */
  private updateMuzzleFlash(dtSec: number): void {
    if (this.muzzleFlashTimer > 0) {
      this.muzzleFlashTimer -= dtSec;
      if (this.muzzleFlashTimer <= 0 && this.muzzleFlash) {
        this.muzzleFlash.intensity = 0;
      }
    }
  }

  /**
   * Handle aiming - rotate the head to face the mouse cursor.
   *
   * The cone always points toward the mouse position in world space.
   */
  private handleAiming(): void {
    const mouseWorld = this.input.getMouseWorldPosition();

    // Calculate angle from current position to mouse position
    const dx = mouseWorld.x - this.position.x;
    const dy = mouseWorld.y - this.position.y;

    // Calculate rotation angle
    // With the tilted camera, game Y is inverted on screen (negative Y = up on screen)
    // atan2 gives us the angle in radians from -PI to PI measured from +X axis
    // We negate dy to account for the inverted Y axis
    // In our setup, the cone points along local +Z after rotation.x is applied
    // When mesh.rotation.y = θ, local +Z points at angle (θ + π/2) in world XZ
    // So to point toward the mouse: θ = atan2(-dy, dx) - π/2
    this.rotation = Math.atan2(-dy, dx) - Math.PI / 2;
  }

  /**
   * Handle shooting - fire projectiles when mouse is held down.
   *
   * Only fires if:
   * - A weapon is equipped
   * - Fire cooldown has elapsed
   * - Mouse button is held down
   *
   * Fire rate is determined by the equipped weapon.
   */
  private handleShooting(dtSec: number): void {
    // Can't shoot without a weapon
    if (!this.weapon) {
      return;
    }

    // Check if mouse is held down and cooldown is ready
    if (this.input.isMouseDown() && this.fireCooldown <= 0) {
      const weaponData = this.weapon.data as WeaponPartData;

      // Fire projectile(s)
      this.fireWeapon(weaponData);

      // Show muzzle flash
      this.showMuzzleFlash();

      // Reset cooldown based on fire rate
      this.fireCooldown = 1 / weaponData.fireRate;
    }
  }

  /**
   * Fire the weapon, spawning projectile(s) based on weapon stats.
   *
   * @param weaponData - The weapon part data containing fire pattern info
   */
  private fireWeapon(weaponData: WeaponPartData): void {
    // Check if we have enough energy to fire
    if (!this.useEnergy(weaponData.energyCost)) {
      // Not enough energy, can't fire
      return;
    }

    // Play shoot sound
    audio.shoot();

    const { projectilesPerShot, spread, projectileSpeed, damage, projectileLifetime } = weaponData;

    // Get the mouse position to aim at
    const mouseWorld = this.input.getMouseWorldPosition();
    const dx = mouseWorld.x - this.position.x;
    const dy = mouseWorld.y - this.position.y;
    const baseAngle = Math.atan2(dy, dx);

    // Spawn multiple projectiles if needed (for spread weapons)
    for (let i = 0; i < projectilesPerShot; i++) {
      let angle = baseAngle;

      // Apply spread if weapon has it
      if (spread > 0 && projectilesPerShot > 1) {
        // Convert spread from degrees to radians
        const spreadRad = (spread * Math.PI) / 180;

        // Distribute projectiles evenly across the spread cone
        // For 3 projectiles with 30° spread: -15°, 0°, +15°
        const offset = (i / (projectilesPerShot - 1) - 0.5) * spreadRad;
        angle = baseAngle + offset;
      }

      // Build projectile config from weapon data using new modifier system
      // Convert old behavior field to modifiers if present, or use modifiers directly
      const modifiers = weaponData.modifiers || this.buildModifiersFromLegacy(weaponData);

      const config: ProjectileConfig = {
        modifiers,
        arcHeight: weaponData.arcHeight,
        color: weaponData.projectileColor,
      };

      // Create and spawn projectile
      const projectile = new Projectile(
        this.position.x,
        this.position.y,
        angle,
        projectileSpeed,
        damage,
        projectileLifetime,
        config
      );

      // Set owner to prevent self-collision
      projectile.setOwner(this);

      // Set room for wall collision detection
      projectile.setRoom(this.room);

      // Set impact effect callback
      projectile.setOnDestroy((x, y) => {
        this.game.spawnImpactEffect(x, y);
      });

      // Set all callbacks - projectile will use what it needs based on modifiers
      projectile.setFindTarget((x, y, owner) => this.game.findNearestEnemy(x, y));

      projectile.setSplitCallback((x, y, splitAngle, splitDamage, parentModifiers) => {
        return this.spawnSplitProjectile(x, y, splitAngle, splitDamage, projectileSpeed, projectileLifetime, parentModifiers);
      });

      projectile.setFindTargetsInRange((x, y, radius, owner, exclude) =>
        this.game.findEnemiesInRange(x, y, radius, exclude)
      );

      projectile.setDealDamage((target, dmg) => {
        this.game.dealDamageToEnemy(target, dmg);
      });

      projectile.setSpawnEffect((x, y, type, radius) => {
        this.game.spawnProjectileEffect(x, y, type, radius);
      });

      this.game.addEntity(projectile);
    }
  }

  /**
   * Convert legacy behavior field to new modifiers system.
   * This maintains backwards compatibility during migration.
   */
  private buildModifiersFromLegacy(weaponData: WeaponPartData): ProjectileModifiers {
    const modifiers: ProjectileModifiers = {};

    switch (weaponData.behavior) {
      case 'homing':
        modifiers.homing = { strength: weaponData.homingStrength || 3 };
        break;
      case 'bouncing':
        modifiers.bouncing = { maxBounces: weaponData.maxBounces || 4 };
        break;
      case 'piercing':
        modifiers.piercing = { maxPierces: weaponData.pierceCount || 2 };
        break;
      case 'splitting':
        modifiers.splitting = { count: weaponData.splitCount || 4, inheritModifiers: true };
        break;
      case 'arcing':
        // Arcing = explosive modifier (AOE on impact)
        modifiers.explosive = {
          radius: weaponData.explosionRadius || 2,
          falloff: 0.5,
        };
        break;
      case 'chain':
        modifiers.chain = {
          targets: weaponData.chainTargets || 3,
          range: weaponData.chainRange || 3,
          falloff: 0.8,
        };
        break;
    }

    return modifiers;
  }

  /**
   * Spawn a split projectile (from splitting weapons).
   * Returns the projectile for potential further configuration.
   */
  private spawnSplitProjectile(
    x: number,
    y: number,
    angle: number,
    damage: number,
    speed: number,
    lifetime: number,
    inheritedModifiers?: ProjectileModifiers
  ): Projectile {
    const projectile = new Projectile(x, y, angle, speed, damage, lifetime * 0.5, {
      modifiers: inheritedModifiers,
      color: 0xff9800, // Orange for split projectiles
    });

    projectile.setOwner(this);
    projectile.setRoom(this.room);
    projectile.setOnDestroy((px, py) => {
      this.game.spawnImpactEffect(px, py);
    });

    // Set all callbacks for inherited modifiers
    projectile.setFindTarget((px, py, owner) => this.game.findNearestEnemy(px, py));
    projectile.setFindTargetsInRange((px, py, radius, owner, exclude) =>
      this.game.findEnemiesInRange(px, py, radius, exclude)
    );
    projectile.setDealDamage((target, dmg) => {
      this.game.dealDamageToEnemy(target, dmg);
    });
    projectile.setSpawnEffect((px, py, type, radius) => {
      this.game.spawnProjectileEffect(px, py, type, radius);
    });

    this.game.addEntity(projectile);
    return projectile;
  }

  /**
   * Get the collision shape for this mech.
   * Mechs use a circle collision shape.
   *
   * @returns Circle collision shape centered on mech position
   */
  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }
}
